import { useSession } from '@rewardops/auth'

import { useApi } from './use-api'

/**
 * Custom hook to get the member's UUID from the PII server (Pangea).
 *
 * This hook retrieves the RewardOps internal UUID for the authenticated member
 * by making a call to the PII server. The UUID is used for analytics tracking
 * and other internal operations.
 *
 * Flow:
 * 1. User authenticates and gets a session with member.id (foreign_id/program membership ID)
 * 2. This hook calls getMemberUUID API endpoint
 * 3. API calls PII server (Pangea) with foreign_id to get the mapping
 * 4. PII server returns the internal UUID from the member mapping table
 *
 * @returns {string | undefined} The member's UUID, or undefined if not available
 */
export const useMemberUUID = (): string | undefined => {
  const { api, tenant } = useApi()
  const { data: session } = useSession()

  const { data: memberData } = api.member.getMemberUUID.useQuery(undefined, {
    enabled:
      !!tenant?.analyticsEnabled &&
      !!session?.user &&
      tenant?.theme !== 'pointsAtWork',
    staleTime: 60 * 60 * 1000, // 1 hour
    cacheTime: 60 * 60 * 1000, // 1 hour
    refetchOnWindowFocus: false,
  })

  return memberData?.memberUUID
}
